/**
 * routing.ts — OSRM (Open Source Routing Machine) Client
 *
 * Berechnet echte Straßen-Distanzen zwischen zwei Koordinaten. Nutzt
 * den öffentlichen Demo-Server von OSRM (router.project-osrm.org).
 * Dieser ist kostenlos ohne API-Key, aber ohne SLA — für Produktion
 * sollte DRPX später einen eigenen OSRM-Server auf Hetzner hosten.
 *
 * Fallback-Strategie:
 *   1. OSRM-Anfrage
 *   2. Bei Fehler/Timeout → null (Caller nutzt dann Haversine-Näherung)
 *
 * Dokumentation OSRM Table Service:
 *   https://project-osrm.org/docs/v5.24.0/api/#route-service
 *
 * @module lib/routing
 */

import type { GeoPoint } from './geo.js';

/**
 * Base-URL des öffentlichen OSRM Demo-Servers.
 * Später ggf. via ENV-Variable überschreiben für Self-Hosting.
 */
const OSRM_BASE_URL = 'https://router.project-osrm.org';

/**
 * Timeout pro OSRM-Anfrage. Demo-Server ist meist schnell (<500ms),
 * aber bei Last kann's länger dauern. 5 Sekunden sind ein guter
 * Kompromiss zwischen Geduld und User-Experience.
 */
const OSRM_TIMEOUT_MS = 5000;

/**
 * Berechnet die echte Straßen-Distanz zwischen zwei GPS-Koordinaten
 * via OSRM-Routing-API.
 *
 * @param from - Startkoordinate (Abholung)
 * @param to   - Zielkoordinate (Ziel)
 * @returns    - Distanz in Kilometern (gerundet) oder null bei Fehler.
 *               null bedeutet: Caller sollte auf Haversine-Fallback
 *               ausweichen, NICHT die Anzeige verstecken.
 *
 * Beispiel:
 *   const km = await fetchRouteDistance(
 *     { lat: 52.52, lng: 13.40 }, // Berlin
 *     { lat: 48.14, lng: 11.58 }, // München
 *   );
 *   // → 585 (km)
 */
export async function fetchRouteDistance(
  from: GeoPoint,
  to: GeoPoint,
): Promise<number | null> {
  // Sanity-Check: Gültige Koordinaten?
  if (
    typeof from.lat !== 'number' ||
    typeof from.lng !== 'number' ||
    typeof to.lat !== 'number' ||
    typeof to.lng !== 'number' ||
    !isFinite(from.lat) ||
    !isFinite(from.lng) ||
    !isFinite(to.lat) ||
    !isFinite(to.lng)
  ) {
    return null;
  }

  // OSRM-URL zusammenbauen: /route/v1/driving/{lng1},{lat1};{lng2},{lat2}
  // Wichtig: Reihenfolge ist LNG,LAT (nicht LAT,LNG — OSRM folgt GeoJSON)
  const coords =
    from.lng + ',' + from.lat + ';' + to.lng + ',' + to.lat;
  const url =
    OSRM_BASE_URL +
    '/route/v1/driving/' +
    coords +
    '?overview=false&alternatives=false&steps=false';

  // AbortController für Timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), OSRM_TIMEOUT_MS);

  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!response.ok) {
      // OSRM liefert 400 bei ungültigen Koordinaten (z.B. im Ozean)
      console.warn('OSRM response not ok:', response.status);
      return null;
    }

    const data: unknown = await response.json();
    if (
      typeof data !== 'object' ||
      data === null ||
      !('routes' in data) ||
      !Array.isArray((data as { routes: unknown[] }).routes)
    ) {
      return null;
    }

    const routes = (data as { routes: Array<{ distance?: number }> }).routes;
    if (routes.length === 0) return null;

    const distanceMeters = routes[0].distance;
    if (typeof distanceMeters !== 'number' || !isFinite(distanceMeters)) {
      return null;
    }

    // OSRM liefert Meter, wir wollen Kilometer (gerundet)
    return Math.round(distanceMeters / 1000);
  } catch (e: unknown) {
    clearTimeout(timeoutId);
    // AbortError (Timeout) oder Netzwerkfehler → null, kein Throw
    if (e instanceof Error && e.name === 'AbortError') {
      console.warn('OSRM timeout');
    } else {
      console.warn('OSRM error:', e);
    }
    return null;
  }
}
